<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import TextArea from 'next/textarea/TextArea.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const { currentAccount, updateAccount } = useAccount();

const categoriesInput = ref('');
const typesInput = ref('');
const isSubmitting = ref(false);

const parseOptions = value =>
  Array.from(
    new Set(
      value
        .split(/\r?\n|,/)
        .map(item => item.trim())
        .filter(Boolean)
    )
  );

const configuredItems = computed(() => ({
  categories: parseOptions(categoriesInput.value),
  types: parseOptions(typesInput.value),
}));

watch(
  currentAccount,
  () => {
    const settings = currentAccount.value?.settings || {};
    categoriesInput.value = (settings.closing_categories || []).join('\n');
    typesInput.value = (settings.closing_types || []).join('\n');
  },
  { deep: true, immediate: true }
);

const handleSubmit = async () => {
  try {
    isSubmitting.value = true;
    await updateAccount(
      {
        closing_categories: configuredItems.value.categories,
        closing_types: configuredItems.value.types,
      },
      { silent: true }
    );
    useAlert(t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.SAVE.SUCCESS'));
  } catch (error) {
    useAlert(t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.SAVE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full outline-1 outline outline-n-container rounded-xl bg-n-solid-2 divide-y divide-n-weak"
  >
    <div class="flex flex-col gap-2 items-start px-5 py-4">
      <h3 class="text-heading-2 text-n-slate-12">
        {{ t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.TITLE') }}
      </h3>
      <p class="mb-0 text-body-para text-n-slate-11">
        {{ t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.DESCRIPTION') }}
      </p>
    </div>

    <div class="px-5 py-4">
      <form class="grid gap-5" @submit.prevent="handleSubmit">
        <WithLabel
          :label="t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.CATEGORIES.LABEL')"
          :help-message="
            t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.CATEGORIES.HELP')
          "
        >
          <TextArea
            v-model="categoriesInput"
            class="w-full"
            :placeholder="
              t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.CATEGORIES.PLACEHOLDER')
            "
          />
        </WithLabel>

        <WithLabel
          :label="t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.TYPES.LABEL')"
          :help-message="t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.TYPES.HELP')"
        >
          <TextArea
            v-model="typesInput"
            class="w-full"
            :placeholder="
              t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.TYPES.PLACEHOLDER')
            "
          />
        </WithLabel>

        <div class="flex gap-2">
          <NextButton
            blue
            type="submit"
            :is-loading="isSubmitting"
            :label="t('CONVERSATION_WORKFLOW.CLOSING_FIELDS.SAVE.BUTTON')"
          />
        </div>
      </form>
    </div>
  </div>
</template>
